import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate("/portal");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#f7f7f5]" data-testid="login-page">
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden bg-white">
        <div className="absolute inset-0 opacity-90"
             style={{background:
              "radial-gradient(1200px 700px at -10% 20%, rgba(11,92,196,0.10), transparent 60%), radial-gradient(900px 500px at 120% 80%, rgba(212,160,23,0.14), transparent 60%)"}} />
        <div className="relative z-10 flex flex-col justify-between p-12">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="JJ" className="w-16 h-16 object-contain" />
            <div>
              <div className="font-display text-2xl text-[#0b5cc4]">JAI JAGDAMBA</div>
              <div className="tracking-[.35em] text-xs text-[#d4a017] font-semibold">LOGISTICS</div>
            </div>
          </div>
          <div>
            <h1 className="font-display text-5xl leading-tight text-slate-900">We Carry <br/><span className="brand-gradient-text">Your Trust.</span></h1>
            <p className="mt-6 text-slate-600 max-w-md">
              Manage consignment notes, freight bills, and memos with print-perfect
              layouts, auto-numbering, and party masters — everything your logistics
              office needs in one place.
            </p>
          </div>
          <div className="text-xs text-slate-500">© {new Date().getFullYear()} Jai Jagdamba Logistics · Kolkata</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={onSubmit} className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-100 p-8" data-testid="login-form">
          <div className="md:hidden flex items-center gap-3 mb-6">
            <img src={LOGO_URL} alt="JJ" className="w-12 h-12 object-contain" />
            <div className="font-display text-xl text-[#0b5cc4]">JAI JAGDAMBA</div>
          </div>
          <h2 className="text-2xl font-semibold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1">Enter your credentials to continue.</p>

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required autoComplete="off" placeholder="Enter your email" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e)=>setPassword(e.target.value)} required autoComplete="new-password" placeholder="Enter your password" className="pr-10" data-testid="login-password-input" />
                <button type="button" onClick={()=>setShowPassword(v=>!v)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600" aria-label={showPassword ? "Hide password" : "Show password"} data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="w-full btn-primary" data-testid="login-submit-btn">
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </div>

        </form>
      </div>
    </div>
  );
}
